import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, AlertDescription, Badge, Button, Input, Label, Textarea } from '@mono/ui-components';
import { AlertCircle, Edit2, FileText, MessageSquare, Save, X } from 'lucide-react';
import { memo, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortal } from '../../../hooks/usePortal';
import { useUpdateRequirement } from '../../../hooks/useUpdateRequirement';
import { RequirementCategory } from '../../../types/proposal';
import { EntityAuditTrail } from '../../common/EntityAuditTrail';
import { DocumentViewerDrawer } from '../DocumentViewer/DocumentViewerDrawer';
import { useProposalV2Context } from '../v2/ProposalV2Context';
import { StatusDropdown } from './StatusDropdown';
const CATEGORY_LABELS = {
    [RequirementCategory.PROPOSAL_INSTRUCTION]: 'Proposal Instruction',
    [RequirementCategory.FORMAT_REQUIREMENT]: 'Format Requirement',
    [RequirementCategory.SUBMISSION_REQUIREMENT]: 'Submission Requirement',
    [RequirementCategory.SCOPE_REQUIREMENT]: 'Scope Requirement',
    [RequirementCategory.PERFORMANCE_STANDARD]: 'Performance Standard',
    [RequirementCategory.DELIVERABLE]: 'Deliverable',
    [RequirementCategory.TASK_REQUIREMENT]: 'Task Requirement',
    [RequirementCategory.EVALUATION_CRITERION]: 'Evaluation Criterion',
    [RequirementCategory.QUALIFICATION]: 'Qualification',
    [RequirementCategory.COMPLIANCE_REQUIREMENT]: 'Compliance Requirement',
    [RequirementCategory.CLAUSE_FLOWDOWN]: 'Clause Flowdown',
    [RequirementCategory.SECURITY_REQUIREMENT]: 'Security Requirement',
    [RequirementCategory.KEY_PERSONNEL]: 'Key Personnel',
    [RequirementCategory.REPORTING_REQUIREMENT]: 'Reporting Requirement',
    [RequirementCategory.PERIOD_OF_PERFORMANCE]: 'Period of Performance',
    [RequirementCategory.PLACE_OF_PERFORMANCE]: 'Place of Performance',
    [RequirementCategory.CLIN]: 'CLIN',
    [RequirementCategory.OTHER]: 'Other'
};
const STATUS_COLORS = {
    NOT_STARTED: 'bg-gray-100 text-gray-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    ADDRESSED: 'bg-green-100 text-green-700',
    PARTIAL: 'bg-yellow-100 text-yellow-700',
    NON_COMPLIANT: 'bg-red-100 text-red-700',
    'N/A': 'bg-gray-100 text-gray-500'
};
const STATUS_LABELS = {
    NOT_STARTED: 'Not Started',
    IN_PROGRESS: 'In Progress',
    ADDRESSED: 'Addressed',
    PARTIAL: 'Partial',
    NON_COMPLIANT: 'Non-Compliant',
    'N/A': 'N/A'
};
export const ComplianceDetailDrawer = /*#__PURE__*/ memo(function ComplianceDetailDrawer({ requirement, projectId, isOpen, onClose }) {
    const portalContainer = usePortal('compliance-detail-drawer-portal');
    const { setAiContext, setSidebarOpen } = useProposalV2Context();
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState({
        status: requirement.status,
        statusNote: '',
        mapping: {
            proposalSection: requirement.mapping?.proposalSection || '',
            responseText: requirement.mapping?.responseText || '',
            pageReference: requirement.mapping?.pageReference || ''
        },
        notes: requirement.notes || '',
        tags: requirement.tags?.join(', ') || ''
    });
    const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
    const updateRequirement = useUpdateRequirement(projectId);
    // Build source document reference from requirement source data
    const sourceDocument = useMemo(()=>{
        const src = requirement.source;
        if (!src?.documentId) return null;
        return {
            documentId: src.documentId,
            filename: src.filename || 'Source Document',
            documentType: src.documentType
        };
    }, [
        requirement.source
    ]);
    // Reset form data when requirement changes
    useEffect(()=>{
        setFormData({
            status: requirement.status,
            statusNote: '',
            mapping: {
                proposalSection: requirement.mapping?.proposalSection || '',
                responseText: requirement.mapping?.responseText || '',
                pageReference: requirement.mapping?.pageReference || ''
            },
            notes: requirement.notes || '',
            tags: requirement.tags?.join(', ') || ''
        });
        setIsEditMode(false);
    }, [
        requirement
    ]);
    const handleSave = async ()=>{
        try {
            const request = {
                requirementId: requirement.requirementId,
                status: formData.status,
                statusNote: formData.statusNote || undefined,
                mapping: {
                    proposalSection: formData.mapping.proposalSection || undefined,
                    responseText: formData.mapping.responseText || undefined,
                    pageReference: formData.mapping.pageReference || undefined
                },
                notes: formData.notes || undefined,
                tags: formData.tags ? formData.tags.split(',').map((t)=>t.trim()).filter(Boolean) : undefined
            };
            await updateRequirement.mutateAsync(request);
            setIsEditMode(false);
        } catch (error) {
            console.error('Failed to update requirement:', error);
        }
    };
    const handleCancel = ()=>{
        setFormData({
            status: requirement.status,
            statusNote: '',
            mapping: {
                proposalSection: requirement.mapping?.proposalSection || '',
                responseText: requirement.mapping?.responseText || '',
                pageReference: requirement.mapping?.pageReference || ''
            },
            notes: requirement.notes || '',
            tags: requirement.tags?.join(', ') || ''
        });
        setIsEditMode(false);
    };
    const handleAskAI = ()=>{
        const label = requirement.source.exactText ? `Requirement ${requirement.requirementId}: ${requirement.source.exactText.slice(0, 120)}${requirement.source.exactText.length > 120 ? '…' : ''}` : `Requirement ${requirement.requirementId}`;
        setAiContext({
            workspace: 'compliance',
            label,
            requirementId: requirement.requirementId
        });
        setSidebarOpen(true);
    };
    const handleClose = ()=>{
        if (isEditMode) {
            handleCancel();
        }
        onClose();
    };
    if (!isOpen || !portalContainer) {
        return null;
    }
    const categoryLabel = CATEGORY_LABELS[requirement.category] || requirement.category;
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity",
                onClick: handleClose
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "fixed inset-y-0 right-0 w-full max-w-3xl bg-white shadow-xl z-50 flex flex-col",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "px-6 py-4 border-b border-gray-200 flex items-start justify-between",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-xl font-semibold",
                                        children: requirement.requirementId
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2 mt-1",
                                        children: [
                                            /*#__PURE__*/ _jsx(Badge, {
                                                variant: "outline",
                                                className: "text-xs",
                                                children: categoryLabel
                                            }),
                                            /*#__PURE__*/ _jsx(Badge, {
                                                variant: "outline",
                                                className: `text-xs font-medium ${requirement.isMandatory ? 'bg-red-100 text-red-700 border-red-300' : 'bg-blue-100 text-blue-700 border-blue-300'}`,
                                                children: requirement.obligationStrength
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs(Button, {
                                        size: "sm",
                                        variant: "outline",
                                        onClick: handleAskAI,
                                        children: [
                                            /*#__PURE__*/ _jsx(MessageSquare, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Ask AI"
                                        ]
                                    }),
                                    isEditMode ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs(Button, {
                                                size: "sm",
                                                onClick: handleSave,
                                                disabled: updateRequirement.isPending,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Save, {
                                                        className: "w-4 h-4 mr-2"
                                                    }),
                                                    "Save"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs(Button, {
                                                size: "sm",
                                                variant: "outline",
                                                onClick: handleCancel,
                                                children: [
                                                    /*#__PURE__*/ _jsx(X, {
                                                        className: "w-4 h-4 mr-2"
                                                    }),
                                                    "Cancel"
                                                ]
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsxs(Button, {
                                        size: "sm",
                                        variant: "outline",
                                        onClick: ()=>setIsEditMode(true),
                                        children: [
                                            /*#__PURE__*/ _jsx(Edit2, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Edit"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: handleClose,
                                        children: /*#__PURE__*/ _jsx(X, {
                                            className: "w-4 h-4"
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 overflow-y-auto px-6 py-6",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-6",
                            children: [
                                updateRequirement.isError && /*#__PURE__*/ _jsxs(Alert, {
                                    variant: "destructive",
                                    children: [
                                        /*#__PURE__*/ _jsx(AlertCircle, {
                                            className: "h-4 w-4"
                                        }),
                                        /*#__PURE__*/ _jsxs(AlertDescription, {
                                            children: [
                                                "Failed to update requirement:",
                                                ' ',
                                                updateRequirement.error instanceof Error ? updateRequirement.error.message : 'Unknown error'
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Status"
                                        }),
                                        isEditMode ? /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(StatusDropdown, {
                                                    value: formData.status,
                                                    onChange: (status)=>setFormData({
                                                            ...formData,
                                                            status
                                                        })
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            htmlFor: "statusNote",
                                                            children: "Status Note"
                                                        }),
                                                        /*#__PURE__*/ _jsx(Textarea, {
                                                            id: "statusNote",
                                                            value: formData.statusNote,
                                                            onChange: (e)=>setFormData({
                                                                    ...formData,
                                                                    statusNote: e.target.value
                                                                }),
                                                            rows: 2,
                                                            placeholder: "Optional note about this status change...",
                                                            className: "mt-1"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }) : /*#__PURE__*/ _jsx(Badge, {
                                            variant: "secondary",
                                            className: `text-sm ${STATUS_COLORS[requirement.status] || 'bg-gray-100 text-gray-700'}`,
                                            children: STATUS_LABELS[requirement.status] || requirement.status
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Requirement Text"
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border",
                                            children: requirement.source.exactText
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between",
                                            children: [
                                                /*#__PURE__*/ _jsx("h3", {
                                                    className: "text-lg font-semibold",
                                                    children: "Your Response"
                                                }),
                                                !isEditMode && formData.mapping.responseText && /*#__PURE__*/ _jsx("button", {
                                                    type: "button",
                                                    onClick: ()=>setIsEditMode(true),
                                                    className: "text-xs text-blue-600 hover:text-blue-700",
                                                    children: "Edit"
                                                })
                                            ]
                                        }),
                                        isEditMode ? /*#__PURE__*/ _jsx(Textarea, {
                                            value: formData.mapping.responseText,
                                            onChange: (e)=>setFormData({
                                                    ...formData,
                                                    mapping: {
                                                        ...formData.mapping,
                                                        responseText: e.target.value
                                                    }
                                                }),
                                            rows: 4,
                                            placeholder: "Describe how this requirement will be addressed in your proposal...",
                                            className: "text-sm"
                                        }) : formData.mapping.responseText ? /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-700 whitespace-pre-wrap bg-blue-50 p-4 rounded-lg border border-blue-100",
                                            children: formData.mapping.responseText
                                        }) : /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            onClick: ()=>setIsEditMode(true),
                                            className: "w-full text-left text-sm text-gray-400 bg-gray-50 p-4 rounded-lg border border-dashed border-gray-300 hover:border-blue-300 hover:text-blue-500 transition-colors",
                                            children: "Click to add a response describing how this requirement will be addressed…"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Classification"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Category"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1",
                                                            children: /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                children: categoryLabel
                                                            })
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Obligation Strength"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1",
                                                            children: /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                className: requirement.isMandatory ? 'bg-red-100 text-red-700 border-red-300' : 'bg-blue-100 text-blue-700 border-blue-300',
                                                                children: requirement.obligationStrength
                                                            })
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Response Action"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1",
                                                            children: requirement.responseAction ? /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                children: requirement.responseAction.replace(/_/g, ' ')
                                                            }) : /*#__PURE__*/ _jsx("span", {
                                                                className: "text-gray-400",
                                                                children: "Not classified"
                                                            })
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Deliverable Type"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1",
                                                            children: requirement.deliverableType ? /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                children: requirement.deliverableType
                                                            }) : /*#__PURE__*/ _jsx("span", {
                                                                className: "text-gray-400",
                                                                children: "Not classified"
                                                            })
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Action Owner"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1",
                                                            children: requirement.actionOwner ? /*#__PURE__*/ _jsx(Badge, {
                                                                variant: "outline",
                                                                children: requirement.actionOwner.replace(/_/g, ' ')
                                                            }) : /*#__PURE__*/ _jsx("span", {
                                                                className: "text-gray-400",
                                                                children: "Not classified"
                                                            })
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Mandatory"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: requirement.isMandatory ? 'Yes' : 'No'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Confidence"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: [
                                                                (requirement.confidence * 100).toFixed(0),
                                                                "%"
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                requirement.isEvaluated && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Evaluation Information"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Factor"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: requirement.evaluationFactor || 'Not specified'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Subfactor"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: requirement.evaluationSubfactor || 'Not specified'
                                                        })
                                                    ]
                                                }),
                                                requirement.evaluationWeight != null && /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Weight"
                                                        }),
                                                        /*#__PURE__*/ _jsxs("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: [
                                                                requirement.evaluationWeight,
                                                                "%"
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Proposal Mapping"
                                        }),
                                        isEditMode ? /*#__PURE__*/ _jsxs("div", {
                                            className: "space-y-3",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            htmlFor: "proposalSection",
                                                            children: "Proposal Section"
                                                        }),
                                                        /*#__PURE__*/ _jsx(Input, {
                                                            id: "proposalSection",
                                                            value: formData.mapping.proposalSection,
                                                            onChange: (e)=>setFormData({
                                                                    ...formData,
                                                                    mapping: {
                                                                        ...formData.mapping,
                                                                        proposalSection: e.target.value
                                                                    }
                                                                }),
                                                            placeholder: "e.g., Technical Volume, Section 3.2",
                                                            className: "mt-1"
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            htmlFor: "pageReference",
                                                            children: "Page Reference"
                                                        }),
                                                        /*#__PURE__*/ _jsx(Input, {
                                                            id: "pageReference",
                                                            value: formData.mapping.pageReference,
                                                            onChange: (e)=>setFormData({
                                                                    ...formData,
                                                                    mapping: {
                                                                        ...formData.mapping,
                                                                        pageReference: e.target.value
                                                                    }
                                                                }),
                                                            placeholder: "e.g., pp. 12-15",
                                                            className: "mt-1"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }) : /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-3 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Proposal Section"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: requirement.mapping?.proposalSection || /*#__PURE__*/ _jsx("span", {
                                                                className: "text-gray-400",
                                                                children: "Not mapped"
                                                            })
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Page Reference"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: requirement.mapping?.pageReference || /*#__PURE__*/ _jsx("span", {
                                                                className: "text-gray-400",
                                                                children: "Not set"
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Notes"
                                        }),
                                        isEditMode ? /*#__PURE__*/ _jsx(Textarea, {
                                            value: formData.notes,
                                            onChange: (e)=>setFormData({
                                                    ...formData,
                                                    notes: e.target.value
                                                }),
                                            rows: 3,
                                            placeholder: "Add notes about this requirement..."
                                        }) : /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-700 whitespace-pre-wrap",
                                            children: requirement.notes || /*#__PURE__*/ _jsx("span", {
                                                className: "text-gray-400",
                                                children: "No notes"
                                            })
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-2 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Tags"
                                        }),
                                        isEditMode ? /*#__PURE__*/ _jsx(Input, {
                                            value: formData.tags,
                                            onChange: (e)=>setFormData({
                                                    ...formData,
                                                    tags: e.target.value
                                                }),
                                            placeholder: "Comma-separated tags..."
                                        }) : requirement.tags && requirement.tags.length > 0 ? /*#__PURE__*/ _jsx("div", {
                                            className: "flex flex-wrap gap-1",
                                            children: requirement.tags.map((tag, i)=>/*#__PURE__*/ _jsx(Badge, {
                                                    variant: "secondary",
                                                    className: "text-xs",
                                                    children: tag
                                                }, i))
                                        }) : /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-gray-400",
                                            children: "No tags"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsx("h3", {
                                            className: "text-lg font-semibold",
                                            children: "Source Information"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "grid grid-cols-2 gap-4 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Document"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: requirement.source.filename || 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Section"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: requirement.source.sectionNumber || 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Page"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700",
                                                            children: requirement.source.pageNumber != null ? requirement.source.pageNumber : 'Not available'
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Label, {
                                                            children: "Block ID"
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "mt-1 text-gray-700 font-mono text-xs",
                                                            children: requirement.source.blockId || 'Not available'
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        requirement.source.documentId && /*#__PURE__*/ _jsxs(Button, {
                                            variant: "outline",
                                            className: "w-full mt-4",
                                            onClick: ()=>setIsDocViewerOpen(true),
                                            disabled: !sourceDocument,
                                            children: [
                                                /*#__PURE__*/ _jsx(FileText, {
                                                    className: "w-4 h-4 mr-2"
                                                }),
                                                sourceDocument ? 'View in Document' : 'Source Document Not Available'
                                            ]
                                        })
                                    ]
                                }),
                                requirement.statusHistory && requirement.statusHistory.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4 border-t pt-4",
                                    children: [
                                        /*#__PURE__*/ _jsxs("h3", {
                                            className: "text-lg font-semibold",
                                            children: [
                                                "Status History (",
                                                requirement.statusHistory.length,
                                                ")"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "space-y-3",
                                            children: [
                                                ...requirement.statusHistory
                                            ].reverse().map((entry, idx)=>/*#__PURE__*/ _jsxs("div", {
                                                    className: "border border-gray-200 rounded-lg p-3 bg-gray-50",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center gap-2",
                                                            children: [
                                                                /*#__PURE__*/ _jsx(Badge, {
                                                                    variant: "secondary",
                                                                    className: `text-xs ${STATUS_COLORS[entry.status] || 'bg-gray-100 text-gray-700'}`,
                                                                    children: STATUS_LABELS[entry.status] || entry.status
                                                                }),
                                                                entry.changedBy && /*#__PURE__*/ _jsxs("span", {
                                                                    className: "text-xs text-gray-500",
                                                                    children: [
                                                                        "by ",
                                                                        entry.changedBy
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-xs text-gray-400",
                                                                    children: new Date(entry.changedAt).toLocaleDateString()
                                                                })
                                                            ]
                                                        }),
                                                        entry.note && /*#__PURE__*/ _jsx("p", {
                                                            className: "text-xs text-gray-600 mt-1",
                                                            children: entry.note
                                                        })
                                                    ]
                                                }, idx))
                                        })
                                    ]
                                }),
                                (()=>{
                                    const amendmentInfo = requirement.amendmentInfo;
                                    if (!amendmentInfo?.isFromAmendment) return null;
                                    return /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-4 border-t pt-4",
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-lg font-semibold",
                                                children: "Amendment Information"
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "grid grid-cols-2 gap-4 text-sm",
                                                children: [
                                                    amendmentInfo.amendmentNumber && /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Label, {
                                                                children: "Amendment Number"
                                                            }),
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "mt-1 text-gray-700",
                                                                children: amendmentInfo.amendmentNumber
                                                            })
                                                        ]
                                                    }),
                                                    amendmentInfo.changeType && /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Label, {
                                                                children: "Change Type"
                                                            }),
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "mt-1",
                                                                children: /*#__PURE__*/ _jsx(Badge, {
                                                                    variant: "outline",
                                                                    className: amendmentInfo.changeType === 'added' ? 'bg-green-100 text-green-700' : amendmentInfo.changeType === 'modified' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700',
                                                                    children: amendmentInfo.changeType
                                                                })
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    });
                                })(),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "border-t pt-4",
                                    children: /*#__PURE__*/ _jsx(EntityAuditTrail, {
                                        projectId: projectId,
                                        resourceType: "requirement",
                                        resourceId: requirement.requirementId
                                    })
                                })
                            ]
                        })
                    })
                ]
            }),
            sourceDocument && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: isDocViewerOpen,
                onClose: ()=>setIsDocViewerOpen(false),
                projectId: projectId,
                document: sourceDocument,
                highlightBlockId: requirement.source.blockId,
                initialPage: requirement.source.pageNumber,
                mode: "extension"
            })
        ]
    }), portalContainer);
});
